/**
 * WP-V2-WC — demo dev pod ?world=1
 * Plansza 3840×720, follow + deadzone, kolizje, minimapa, edge zones.
 * Nie wymaga registry — start przez installWorldDevHook(game).
 */
import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '@pieskowo/shared';
import { setupWorld, follow } from './WorldCamera.js';
import { WalkController } from './WalkController.js';
import { Minimap } from './Minimap.js';
import { EdgeTransition } from './EdgeTransition.js';
import type { ColliderRect, WalkableTarget } from './types.js';

export const WORLD_DEMO_W = 3840;
export const WORLD_DEMO_H = 720;

/** Prosty mock celu — bez zależności od avatar/. */
class DemoPawn implements WalkableTarget {
  readonly container: Phaser.GameObjects.Container;
  private body: Phaser.GameObjects.Arc;
  private label: Phaser.GameObjects.Text;
  lastMoveTo: { x: number; y: number } | null = null;
  lastVelocity = { x: 0, y: 0 };

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.body = scene.add.circle(0, 0, 28, 0xffe4b5).setStrokeStyle(4, 0xffffff);
    const eyeL = scene.add.circle(-10, -6, 4, 0x4a3f55);
    const eyeR = scene.add.circle(10, -6, 4, 0x4a3f55);
    this.label = scene.add
      .text(0, 40, 'Ty', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '16px',
        color: '#4a3f55',
        fontStyle: 'bold',
        backgroundColor: '#ffffffaa',
        padding: { x: 6, y: 2 },
      })
      .setOrigin(0.5);
    this.container = scene.add.container(x, y, [this.body, eyeL, eyeR, this.label]);
    this.container.setDepth(100);
    this.container.setSize(64, 64);
  }

  moveTo(x: number, y: number): void {
    this.lastMoveTo = { x, y };
  }

  setVelocity(vx: number, vy: number): void {
    this.lastVelocity = { x: vx, y: vy };
    if (vx < -2) this.body.setScale(-1, 1);
    else if (vx > 2) this.body.setScale(1, 1);
  }
}

export class WorldDemoScene extends Phaser.Scene {
  static readonly KEY = 'WorldDemo';

  private walk: WalkController | null = null;
  private minimap: Minimap | null = null;
  private edges: EdgeTransition | null = null;
  private statusText: Phaser.GameObjects.Text | null = null;
  private pawn: DemoPawn | null = null;

  constructor() {
    super(WorldDemoScene.KEY);
  }

  create(): void {
    setupWorld(this, { worldW: WORLD_DEMO_W, worldH: WORLD_DEMO_H, bg: 0xc8e6c9 });

    // Trzy „dzielnice" — pastelowe pasy (Rynek / Osiedle / Pole)
    this.add.rectangle(640, 360, 1280, 720, 0xfff3e9).setDepth(-900);
    this.add.rectangle(1920, 360, 1280, 720, 0xe8f5e9).setDepth(-900);
    this.add.rectangle(3200, 360, 1280, 720, 0xe3f2fd).setDepth(-900);

    const labels: Array<{ x: number; text: string }> = [
      { x: 640, text: 'Osiedle (zachód)' },
      { x: 1920, text: 'Rynek Łapki' },
      { x: 3200, text: 'Pole · Stajnia · Basen' },
    ];
    for (const l of labels) {
      this.add
        .text(l.x, 48, l.text, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '28px',
          color: '#4a3f55',
          fontStyle: 'bold',
        })
        .setOrigin(0.5, 0)
        .setDepth(-800);
    }

    // Blokady demonstracyjne (budynki / płoty)
    const colliders: ColliderRect[] = [
      { x: 1700, y: 200, width: 200, height: 160 },
      { x: 900, y: 380, width: 140, height: 120 },
      { x: 2800, y: 280, width: 180, height: 200 },
      { x: 500, y: 450, width: 100, height: 80 },
    ];
    for (const c of colliders) {
      this.add
        .rectangle(c.x + c.width / 2, c.y + c.height / 2, c.width, c.height, 0xc4a484, 0.85)
        .setStrokeStyle(3, 0xffffff)
        .setDepth(10);
      this.add
        .text(c.x + c.width / 2, c.y + c.height / 2, '🧱', {
          fontSize: '28px',
        })
        .setOrigin(0.5)
        .setDepth(11);
    }

    const startX = WORLD_DEMO_W / 2;
    const startY = WORLD_DEMO_H / 2;
    this.pawn = new DemoPawn(this, startX, startY);

    this.walk = new WalkController(this, this.pawn, {
      colliders,
      bounds: {
        minX: 40,
        minY: 80,
        maxX: WORLD_DEMO_W - 40,
        maxY: WORLD_DEMO_H - 40,
      },
      enableInput: true,
    });

    follow(this, this.pawn.container, {
      lerp: 0.12,
      deadzone: { width: 120, height: 80 },
    });

    this.minimap = new Minimap(this, {
      worldW: WORLD_DEMO_W,
      worldH: WORLD_DEMO_H,
      landmarks: [
        { id: 'estate', x: 640, y: 360, color: 0xffcc99, labelPl: 'Osiedle' },
        { id: 'market', x: 1920, y: 360, color: 0xffb6c1, labelPl: 'Rynek' },
        { id: 'field', x: 3200, y: 360, color: 0x90caf9, labelPl: 'Pole' },
      ],
      visible: true,
    });

    this.edges = new EdgeTransition({
      worldW: WORLD_DEMO_W,
      worldH: WORLD_DEMO_H,
      band: 56,
      onEnter: (z) => {
        this.statusText?.setText(`Strefa: ${z.labelPl ?? z.id}`);
      },
      onLeave: () => {
        this.statusText?.setText('Demo świata — tap / WASD / strzałki');
      },
    });
    this.edges.debugDraw(this, 5);

    this.statusText = this.add
      .text(GAME_WIDTH / 2, GAME_HEIGHT - 28, 'Demo świata — tap / WASD / strzałki', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '18px',
        color: '#4a3f55',
        backgroundColor: '#ffffffcc',
        padding: { x: 10, y: 6 },
      })
      .setOrigin(0.5)
      .setScrollFactor(0)
      .setDepth(10_001);

    this.add
      .text(12, 12, 'WP-V2-WC · ?world=1 · 3840×720', {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#6b5f7a',
        backgroundColor: '#ffffffaa',
        padding: { x: 8, y: 4 },
      })
      .setScrollFactor(0)
      .setDepth(10_001);
  }

  update(_t: number, dtMs: number): void {
    const dt = dtMs / 1000;
    this.walk?.update(dt);
    const pos = this.walk?.position;
    if (pos) {
      this.minimap?.setPlayerPosition(pos.x, pos.y);
      this.edges?.update(pos.x, pos.y);
    }
  }

  shutdown(): void {
    this.walk?.destroy();
    this.minimap?.destroy();
    this.edges?.destroy();
    this.walk = null;
    this.minimap = null;
    this.edges = null;
  }
}
